import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Lock } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMarketTimeZone } from '@/features/country-scope';
import { useCustomers } from '@/features/customers';
import { ApiError } from '@/shared/api/api-error';
import { SearchableSelect, type SearchableOption } from '@/shared/components/searchable-select';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';
import { MarketDateTimeField } from '@/shared/components/market-datetime-field';
import { fieldInstant, marketNowInput, toMarketInput } from '@/shared/lib/datetime-local';

import { useCreateCoupon, useUpdateCoupon } from '../api/coupons.queries';
import { couponSchema, type CouponFormValues } from '../schemas/coupon.schema';
import type { Coupon } from '../types/coupons.types';
import { BrandScopeField } from './brand-scope-field';

const FIELDS = [
  'code',
  'customerId',
  'percentOff',
  'maxDiscountAmount',
  'minOrderAmount',
  'brandIds',
  'validFrom',
  'validTo',
  'description',
] as const;
const NO_OPTIONS: SearchableOption[] = [];

function defaults(coupon: Coupon | undefined, zone: string): CouponFormValues {
  return {
    kind: coupon?.kind === 'Personal' ? 'Personal' : 'Public',
    code: coupon?.code ?? '',
    customerId: coupon?.customerId ?? null,
    percentOff: coupon?.percentOff ?? 10,
    maxDiscountAmount: coupon?.maxDiscountAmount ?? null,
    minOrderAmount: coupon?.minOrderAmount ?? null,
    appliesToAllBrands: coupon?.appliesToAllBrands ?? true,
    brandIds: coupon?.brands.map((b) => b.brandId) ?? [],
    validFrom: coupon ? toMarketInput(coupon.validFrom, zone) : marketNowInput(zone),
    validFromOccurrence: null,
    validTo: coupon ? toMarketInput(coupon.validTo, zone) : marketNowInput(zone, 30),
    validToOccurrence: null,
    description: coupon?.description ?? '',
  };
}

/** A number input whose empty value is NaN (required) or null (optional). */
function numberValue(value: number | null) {
  return value === null || Number.isNaN(value) ? '' : value;
}

/**
 * Create a personal or public coupon, or change one. A coupon that has been used (or a points
 * coupon, which the customer bought) keeps its value and brands: only its dates and description
 * can change, and the backend refuses anything else with COUPON_LOCKED. Its window is entered in
 * the coupon's market zone, and untouched dates are saved back exactly as they were.
 */
export function CouponDialog({
  countryId,
  countryName,
  currency,
  coupon,
  onClose,
}: {
  countryId: number;
  countryName: string;
  currency: string | null;
  coupon?: Coupon;
  onClose: () => void;
}) {
  const create = useCreateCoupon(countryId);
  const update = useUpdateCoupon();
  const mutation = coupon ? update : create;
  const locked =
    coupon !== undefined &&
    (coupon.kind === 'Points' ||
      coupon.redeemedCount > 0 ||
      coupon.reservedCount > 0 ||
      coupon.conflictCount > 0);

  const zone = useMarketTimeZone(coupon?.countryId ?? countryId);
  const schema = useMemo(() => couponSchema(zone, coupon), [zone, coupon]);
  const [customerSearch, setCustomerSearch] = useState('');
  const debouncedCustomer = useDebouncedValue(customerSearch, 300);
  const form = useForm<CouponFormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaults(coupon, zone),
  });
  const kind = useWatch({ control: form.control, name: 'kind' });
  const appliesToAllBrands = useWatch({ control: form.control, name: 'appliesToAllBrands' });
  const brandIds = useWatch({ control: form.control, name: 'brandIds' });
  const customers = useCustomers({
    page: 1,
    pageSize: 20,
    search: debouncedCustomer || undefined,
    countryId,
    isActive: true,
  });

  const { error } = mutation;
  useEffect(() => {
    if (!(error instanceof ApiError) || !error.fieldErrors) return;
    for (const [field, message] of Object.entries(error.fieldErrors)) {
      const key = FIELDS.find((name) => name.toLowerCase() === field.toLowerCase());
      if (key) form.setError(key, { type: 'server', message });
    }
  }, [error, form]);
  const formError = error instanceof ApiError && !error.fieldErrors ? error.message : null;

  const customerOptions =
    customers.data?.items.map((c) => ({
      value: String(c.id),
      label: c.name ?? c.userName ?? `#${c.id}`,
      description: c.msisdn ?? undefined,
    })) ?? NO_OPTIONS;

  const submit = (values: CouponFormValues) => {
    const validFrom = fieldInstant(
      coupon?.validFrom,
      values.validFrom,
      values.validFromOccurrence,
      zone,
    );
    const validTo = fieldInstant(coupon?.validTo, values.validTo, values.validToOccurrence, zone);
    // The schema has already resolved both; this only narrows the types.
    if (!validFrom || !validTo) return;
    const shared = {
      percentOff: values.percentOff,
      maxDiscountAmount: values.maxDiscountAmount,
      minOrderAmount: values.minOrderAmount,
      appliesToAllBrands: values.appliesToAllBrands,
      brandIds: values.appliesToAllBrands ? [] : values.brandIds,
      validFrom,
      validTo,
      description: values.description.trim() || null,
    };
    if (coupon) {
      update.mutate({ id: coupon.id, values: shared }, { onSuccess: onClose });
      return;
    }
    create.mutate(
      {
        ...shared,
        kind: values.kind,
        code: values.code.trim() || undefined,
        customerId: values.kind === 'Personal' ? (values.customerId ?? undefined) : undefined,
      },
      { onSuccess: onClose },
    );
  };

  const amountField = (
    name: 'maxDiscountAmount' | 'minOrderAmount',
    label: string,
    description: string,
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              disabled={locked}
              value={numberValue(field.value)}
              onChange={(event) =>
                field.onChange(event.target.value === '' ? null : Number(event.target.value))
              }
            />
          </FormControl>
          <FormDescription>{description}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base">
            {coupon ? `Edit coupon ${coupon.code}` : 'New coupon'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            For customers in {countryName}. Amounts are in {currency ?? 'the settlement currency'}.
            One use per customer; one coupon per checkout.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form noValidate className="space-y-4 text-xs" onSubmit={form.handleSubmit(submit)}>
            {!coupon && (
              <Tabs
                value={kind}
                onValueChange={(value) => form.setValue('kind', value as CouponFormValues['kind'])}
              >
                <TabsList className="w-full">
                  <TabsTrigger value="Public" className="flex-1 text-xs">
                    Every customer
                  </TabsTrigger>
                  <TabsTrigger value="Personal" className="flex-1 text-xs">
                    One customer
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            )}

            {locked && (
              <p className="bg-muted text-muted-foreground flex items-start gap-2 rounded-md p-2">
                <Lock className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                {coupon?.kind === 'Points'
                  ? 'The customer bought this coupon with points: only its dates and description can change.'
                  : 'This coupon has been used, so its value and brands are locked. You can change its dates, or deactivate it and create a new one.'}
              </p>
            )}

            {!coupon && (
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Code</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        autoComplete="off"
                        className="font-mono uppercase"
                        placeholder={kind === 'Personal' ? 'Generated when empty' : 'e.g. SUMMER10'}
                      />
                    </FormControl>
                    <FormDescription>
                      Letters, digits and dashes; customers can type it in any case.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {!coupon && kind === 'Personal' && (
              <FormField
                control={form.control}
                name="customerId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="coupon-customer">Customer</FormLabel>
                    <SearchableSelect
                      id="coupon-customer"
                      label="Customer"
                      value={field.value === null ? null : String(field.value)}
                      options={customerOptions}
                      onChange={(value) => field.onChange(Number(value))}
                      placeholder="Choose a customer"
                      searchPlaceholder="Search name or mobile…"
                      emptyText="No customer matches."
                      search={customerSearch}
                      onSearchChange={setCustomerSearch}
                      loading={customers.isFetching}
                      triggerClassName="h-9 w-full text-sm"
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="percentOff"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Percent off</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      inputMode="decimal"
                      min={1}
                      max={90}
                      step="0.01"
                      disabled={locked}
                      value={numberValue(field.value)}
                      onChange={(event) =>
                        field.onChange(
                          event.target.value === '' ? Number.NaN : Number(event.target.value),
                        )
                      }
                    />
                  </FormControl>
                  <FormDescription>1–90% of the price of every covered item.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              {amountField(
                'maxDiscountAmount',
                'Maximum discount',
                'Per checkout. Empty = no cap.',
              )}
              {amountField('minOrderAmount', 'Minimum order', 'Of covered items. Empty = none.')}
            </div>

            <FormField
              control={form.control}
              name="brandIds"
              render={({ fieldState }) => (
                <FormItem>
                  <BrandScopeField
                    idPrefix="coupon"
                    countryId={countryId}
                    appliesToAllBrands={appliesToAllBrands}
                    brandIds={brandIds}
                    knownBrands={coupon?.brands ?? []}
                    disabled={locked}
                    error={fieldState.error?.message}
                    onChange={(value) => {
                      form.setValue('appliesToAllBrands', value.appliesToAllBrands);
                      form.setValue('brandIds', value.brandIds, {
                        shouldValidate: form.formState.isSubmitted,
                      });
                    }}
                  />
                </FormItem>
              )}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <MarketDateTimeField<CouponFormValues>
                name="validFrom"
                occurrenceName="validFromOccurrence"
                label="Starts"
                zone={zone}
                original={coupon?.validFrom}
              />
              <MarketDateTimeField<CouponFormValues>
                name="validTo"
                occurrenceName="validToOccurrence"
                label="Ends"
                zone={zone}
                original={coupon?.validTo}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Internal note</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g. Summer campaign, PlayStation launch" />
                  </FormControl>
                  <FormDescription>Only staff see this.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {coupon?.marginWarning && (
              <p role="status" className="text-status-warning">
                {coupon.marginWarning}
              </p>
            )}
            {formError && (
              <p role="alert" className="text-destructive">
                {formError}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
                {coupon ? 'Save' : 'Create coupon'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
